/**
 * Every fan-facing string this card renders, declared once so a copy edit
 * that drifts from `issue.md` fails a test instead of shipping.
 *
 * American English, straight ASCII apostrophes, no dash characters, per
 * `human-writing.mdc` and the `checkNoDashesInCopy` guard.
 */
import type { typeConfirmDestructiveInput } from "@sportsmet/ui";

export const BLOCK_ACTION_LABEL = "Block";

export function blockConfirmTitle(displayName: string): string {
  return `Block ${displayName}?`;
}

export const BLOCK_CONFIRM_BODY =
  "They won't be able to message you, and you won't see each other's reactions or new messages in chats you share.";

export const REPORT_ACTION_LABEL = "Report";
export const DELETE_ACTION_LABEL = "Delete";

export const DELETE_CONFIRM_TITLE = "Delete this message?";
export const OWN_DELETE_CONFIRM_BODY = "This can't be undone.";

export function moderatorDeleteConfirmBody(senderDisplayName: string): string {
  return `${senderDisplayName}'s message will be removed for everyone in the chat. This can't be undone.`;
}

export const BLOCKED_MESSAGE_LINE = "Message from a blocked player";
export const SHOW_BLOCKED_MESSAGE_LABEL = "Show";

/** A sheet title is a heading, so it carries no closing period (QA round 1). */
export const REPORT_SHEET_TITLE = "Report this message";

/** The account-scoped variant of `REPORT_SHEET_TITLE`, `Report Marcus` from a
 * profile or a chat member row (`51-message-requests-and-moderation.md` line
 * 110), mirroring `blockConfirmTitle`'s one-argument shape. No closing
 * period: it is a heading, and "Report QA social Pub." read oddly. */
export function reportAccountSheetTitle(displayName: string): string {
  return `Report ${displayName}`;
}

export const REPORT_REASONS = ["spam", "harassment", "hate", "other"] as const;
export type typeReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_REASON_LABELS: Record<typeReportReason, string> = {
  spam: "Spam",
  harassment: "Harassment or bullying",
  hate: "Inappropriate content",
  other: "Something else",
};

export const REPORT_REASONS_GROUP_LABEL = "Why are you reporting this?";
export const REPORT_DETAILS_LABEL = "Add details (optional)";
export const REPORT_DETAILS_MAX_LENGTH = 240;
export const BLOCK_THEM_TOO_LABEL = "Block them too";
export const SEND_REPORT_LABEL = "Send report";
export const REPORT_SENT_LINE = "Report sent. Thanks for letting us know.";
export const REPORT_DONE_LABEL = "Done";

/** Declared for ISSUE-280 to import; nothing in this card renders it. */
export const MESSAGE_DELETED_LINE = "Message deleted";

export function buildBlockConfirm(payload: {
  displayName: string;
}): typeConfirmDestructiveInput {
  return {
    title: blockConfirmTitle(payload.displayName),
    body: BLOCK_CONFIRM_BODY,
    confirmLabel: BLOCK_ACTION_LABEL,
  };
}

export function buildOwnDeleteConfirm(): typeConfirmDestructiveInput {
  return {
    title: DELETE_CONFIRM_TITLE,
    body: OWN_DELETE_CONFIRM_BODY,
    confirmLabel: DELETE_ACTION_LABEL,
  };
}

export function buildModeratorDeleteConfirm(payload: {
  senderDisplayName: string;
}): typeConfirmDestructiveInput {
  return {
    title: DELETE_CONFIRM_TITLE,
    body: moderatorDeleteConfirmBody(payload.senderDisplayName),
    confirmLabel: DELETE_ACTION_LABEL,
  };
}

/** "" and "   " both become null. The report schema rejects an empty string. */
export function normalizeReportDetails(raw: string): string | null {
  return raw.trim() === "" ? null : raw;
}
